import React, { useState, useEffect } from "react";
import { Link, useParams } from "react-router-dom";
import axios from "axios";
import { Play, Calendar, Clock, ChevronRight, Trophy, Eye } from "lucide-react";
import { API } from "@/config";

export default function VideoPlayerPage() {
  const { videoId } = useParams();
  const [video, setVideo] = useState(null);
  const [relatedVideos, setRelatedVideos] = useState([]);
  const [loading, setLoading] = useState(true);
  
  useEffect(() => {
    fetchVideo();
  }, [videoId]); // eslint-disable-line react-hooks/exhaustive-deps -- refetch on video change
  
  const fetchVideo = async () => {
    try {
      const res = await axios.get(`${API}/videos/${videoId}`);
      setVideo(res.data);
      
      const relatedRes = await axios.get(`${API}/videos?season_id=${res.data.season_id}&limit=4`);
      setRelatedVideos(relatedRes.data.filter(v => v.id !== videoId).slice(0, 3));
    } catch (err) {
      console.error("Error fetching video:", err);
    } finally {
      setLoading(false);
    }
  };
  
  const getEmbedUrl = (url) => {
    if (!url) return '';
    if (url.includes('ok.ru/video/')) {
      const vid = url.split('ok.ru/video/')[1]?.split('?')[0];
      return `https://ok.ru/videoembed/${vid}`;
    }
    if (url.includes('ok.ru/videoembed/')) {
      return url;
    }
    return url;
  };
  
  if (loading) {
    return (
      <div className="loading">
        <div className="loading-spinner"></div>
      </div>
    );
  }
  
  if (!video) {
    return (
      <div className="container" style={{ padding: '4rem 1rem' }}>
        <div className="empty-state">
          <h3 className="empty-state-title">Video Not Found</h3>
          <Link to="/vault" className="btn btn-primary" style={{ marginTop: '1rem' }}>
            Back to The Vault
          </Link>
        </div>
      </div>
    );
  }
  
  return (
    <div className="container" style={{ padding: '2rem 1rem 4rem' }}>
      <nav className="breadcrumb">
        <Link to="/vault">The Vault</Link>
        <ChevronRight size={16} className="breadcrumb-separator" />
        <Link to={`/season/${video.season_id}`}>{video.season_year}</Link>
        <ChevronRight size={16} className="breadcrumb-separator" />
        <span>{video.title}</span>
      </nav>
      
      <div className="video-player-container" data-testid="video-player">
        <iframe
          src={getEmbedUrl(video.video_url)}
          allowFullScreen
          allow="autoplay"
          title={video.title}
        />
      </div>
      
      <div className="video-info">
        <h1 className="video-info-title">{video.title}</h1>
        
        <div className="video-info-meta">
          <span className="video-info-meta-item">
            <Calendar size={16} />
            {video.date}
          </span>
          {video.duration && (
            <span className="video-info-meta-item">
              <Clock size={16} />
              {video.duration}
            </span>
          )}
          <span className="video-info-meta-item">
            <Eye size={16} />
            {video.views} views
          </span>
          {video.opponent && (
            <span className="video-info-meta-item">
              <Trophy size={16} />
              vs {video.opponent}
            </span>
          )}
        </div>
        
        {video.description && (
          <p className="video-info-description">{video.description}</p>
        )}
      </div>
      
      {relatedVideos.length > 0 && (
        <section style={{ marginTop: '4rem' }}>
          <h2 className="section-title" style={{ marginBottom: '1.5rem' }}>More from {video.season_year}</h2>
          <div className="video-grid">
            {relatedVideos.map((rv) => (
              <Link 
                key={rv.id} 
                to={`/watch/${rv.id}`} 
                className="video-card"
              >
                <div className="video-card-thumbnail">
                  <img 
                    src={rv.thumbnail || 'https://images.unsplash.com/photo-1546519638-68e109498ffc?w=400&q=80'} 
                    alt={rv.title}
                  />
                  <div className="video-card-play">
                    <div className="video-card-play-icon">
                      <Play size={24} />
                    </div>
                  </div>
                </div>
                <div className="video-card-body">
                  <h3 className="video-card-title">{rv.title}</h3>
                  <div className="video-card-meta">
                    <span className="video-card-meta-item">
                      <Calendar size={14} />
                      {rv.date}
                    </span>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </section>
      )}
    </div>
  );
}
